@switch (type) {
  @case (inputRadioComponentType.REGULAR) {
    <div class="flex justify-start w-full">
      @for (option of options; let i = $index; track i) {
        <label
          class="flex items-center cursor-pointer group"
          [ngClass]="{
            'pl-2': i > 0,
            'opacity-50 select-none pointer-events-none': option.disabled
          }"
        >
          <input
            [id]="option.id ?? (id + '-option-' + i)"
            [name]="name"
            type="radio"
            [value]="option.value"
            [tabindex]="tabIndex"
            class="sr-only"
            [ngModel]="selectedOption()"
            [disabled]="option.disabled ?? false"
            (click)="selectOption(option.value)"
          />

          <div class="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center transition-all">
            @if (isOptionSelected(option.value)) {
              <div class="w-2.5 h-2.5 rounded-full bg-[#3b82f6]"></div>
            }
          </div>

          <span
            class="ml-1 text-sm transition-colors"
            [class.text-gray-400]="!isOptionSelected(option.value)"
            [class.text-white]="isOptionSelected(option.value)"
          >
            {{ option.label }}
          </span>
        </label>
      }
    </div>
  }
  @case (inputRadioComponentType.CARDS) {
    <ul
      class="grid w-full px-16"
      [ngClass]="{
        'grid-cols-1': gridColumns === 1,
        'grid-cols-2': gridColumns === 2,
        'grid-cols-3': gridColumns === 3,
        'grid-cols-4': gridColumns === 4,
        'gap-2': !size || size !== inputRadioComponentSize.EXTRA_LARGE,
        'gap-8 px-16': size === inputRadioComponentSize.EXTRA_LARGE,
      }"
    >
      @for (option of options; let i = $index; track i) {
        <li
          class="relative flex items-center"
          [ngClass]="{
            'h-10': !size || size === inputRadioComponentSize.MEDIUM,
            'h-40': size === inputRadioComponentSize.LARGE,
            'h-80': size === inputRadioComponentSize.EXTRA_LARGE,
            'opacity-50 select-none pointer-events-none': option.disabled
          }"
          [tabindex]="i"
          (click)="selectOption(option.value)"
          (keyup)="null"
        >
          <input
            type="radio"
            class="hidden peer h-full w-full"
            [id]="option.id ?? (id + '-option-' + i)"
            [name]="name"
            [value]="option.value"
            [tabindex]="tabIndex"
            [ngModel]="selectedOption()"
            [disabled]="option.disabled ?? false"
          />

          <label
            [for]="id + '-option-' + i"
            class="inline-flex items-center flex-col justify-center w-full h-full px-2 py-1 border-0 border-white/10 cursor-pointer text-white/50 rounded-10 peer-checked:border-cyan-700 peer-checked:text-white hover:text-white/75"
            [ngClass]="{
              'flex-row gap-2 bg-white/10 hover:bg-white/20 peer-checked:border-1': !size || size === inputRadioComponentSize.MEDIUM,
              'gap-6 bg-transparent-dark border-1 hover:bg-white/10 peer-checked:bg-white/10 peer-checked:border-2': size === inputRadioComponentSize.LARGE,
              'gap-8 bg-transparent-dark border-2 hover:bg-white/15 peer-checked:bg-white/15 peer-checked:border-3': size === inputRadioComponentSize.EXTRA_LARGE
            }"
          >
            @if (option.icon) {
              <nrc-icon
                class="flex items-center justify-center rounded-full shrink-0"
                [ngClass]="{
                  'w-8 h-8 p-2 bg-white/5': !size || size === inputRadioComponentSize.MEDIUM,
                  'w-10 h-10': size === inputRadioComponentSize.LARGE,
                  'w-14 h-14': size === inputRadioComponentSize.EXTRA_LARGE
                }"
                [name]="option.icon | toString"
                [fillColor]="option.value === selectedOption()
                  ? 'cyan'
                  : (size === inputRadioComponentSize.EXTRA_LARGE ? 'white' : 'semi-transparent')"
              />
            }

            <span
              class="font-light tracking-wide text-center"
              [ngClass]="{
                'text-sm': !size || size === inputRadioComponentSize.MEDIUM || size === inputRadioComponentSize.LARGE,
                'text-lg text-white': size === inputRadioComponentSize.EXTRA_LARGE
              }"
            >
              {{ option.label }}
            </span>
          </label>

          @if (option.description) {
            <div class="absolute w-5 h-5 group right-2 top-2">
              <nrc-icon
                class="absolute w-5 h-5 peer"
                [name]="'info-outline'"
                [fillColor]="'cyan'"
              />

              <nrc-tooltip
                class="pt-2 tooltip tooltip-group"
                [text]="option.description"
              />
            </div>
          }
        </li>
      }
    </ul>
  }
  @case (inputRadioComponentType.ROWS) {
    <div class="flex flex-col items-center space-y-1">
      @for (option of options; let i = $index; track i) {
        <div
          class="w-full p-2 border-0 shadow-md cursor-pointer peer bg-white/5 rounded-10 hover:bg-white/10"
          [ngClass]="{
            'border-1 border-cyan-700 bg-white/20': option.value === selectedOption(),
            'opacity-50 select-none pointer-events-none': option.disabled
          }"
        >
          <div class="flex items-center space-x-2">
            <input
              [id]="option.id ?? (id + '-option-' + i)"
              [name]="name"
              type="radio"
              [value]="option.value"
              [tabindex]="tabIndex"
              class="hidden"
              [ngModel]="selectedOption()"
              [disabled]="option.disabled ?? false"
              (click)="selectOption(option.value)"
            />

            @if (option.icon) {
              <div class="flex items-center justify-center p-2 rounded-md bg-white/15">
                <nrc-icon
                  class="flex items-center justify-center rounded-full shrink-0"
                  [ngClass]="{
                    'w-5 h-5 bg-white/5': !size || size === inputRadioComponentSize.MEDIUM,
                    'w-8 h-8': size === inputRadioComponentSize.LARGE,
                    'w-10 h-10': size === inputRadioComponentSize.EXTRA_LARGE
                  }"
                  [name]="option.icon | toString"
                  [fillColor]="option.value === selectedOption() ? 'white' : 'semi-transparent'"
                />
              </div>
            }

            <label
              [for]="option.id ?? (id + '-option-' + i)"
              class="flex flex-col items-start justify-start w-full h-full px-2 py-1 overflow-hidden cursor-pointer text-white/50 text-ellipsis text-nowrap"
            >
              <span
                class="text-sm font-light tracking-wide text-center"
                [ngClass]="{
                  'text-white': option.value === selectedOption()
                }"
              >
                {{ option.label }}
              </span>

              @if (option.description) {
                <span
                  class="text-xs font-light"
                  [ngClass]="{
                    'text-white': option.value === selectedOption()
                  }"
                >
                  {{ option.description }}
                </span>
              }
            </label>
          </div>
        </div>
      }
    </div>
  }
  @case (inputRadioComponentType.COLUMNS) {
    <div class="flex w-full gap-3 flex-nowrap">
      @for (option of options; let i = $index; track i) {
        <!-- Display the "Show all options" as a third option if selected option index is greater than 3 -->
        @if (i === 2 && showExtendedColumnOptions()) {
          <div
            class="relative flex items-center rounded-md shadow-md cursor-pointer group bg-white/5 hover:bg-white/10"
            tabindex="0"
            (click)="toggleShowExtendedColumnOptions()"
            (keyup)="null"
          >
            <div class="flex items-center justify-center p-2 rounded-md bg-white/5 group-hover:bg-white/10">
              <span class="flex items-center justify-center w-5 h-5 text-xl text-white">...</span>
            </div>
            <nrc-tooltip
              class="tooltip tooltip-group"
              [text]="'Show all options'"
            />
          </div>
        }

        <div
          class="relative flex items-center rounded-md shadow-md cursor-pointer group bg-white/5 hover:bg-white/10"
          [tabindex]="i"
          [ngClass]="{
            'hidden': (i !== 0 && selectedOptionIndex() !== i) && showExtendedColumnOptions(),
            'border-1 border-cyan-700': i === selectedOptionIndex(),
            'opacity-50 select-none pointer-events-none': option.disabled
          }"
          (click)="selectOption(option.value)"
          (keyup)="null"
        >
          <input
            type="radio"
            class="hidden"
            [id]="option.id ?? (id + '-option-' + i)"
            [name]="name"
            [value]="option.value"
            [tabindex]="tabIndex"
            [ngModel]="selectedOption()"
            [disabled]="option.disabled ?? false"
            (click)="selectOption(option.value)"
          />

          @if (option.icon) {
            <div class="flex items-center justify-center p-2 rounded-md bg-white/5 group-hover:bg-white/10">
              <nrc-icon
                class="flex items-center justify-center shrink-0"
                [ngClass]="{
                  'w-5 h-5': !size || size === inputRadioComponentSize.MEDIUM,
                  'w-9 h-9': size === inputRadioComponentSize.LARGE,
                  'w-11 h-11': size === inputRadioComponentSize.EXTRA_LARGE
                }"
                [name]="option.icon | toString"
                [fillColor]="option.value === selectedOption() ? 'white' : 'semi-transparent'"
              />
            </div>
          }

          <div
            class="flex items-center transition-[max-width] duration-500 overflow-hidden"
            [ngClass]="{
              'max-w-0': option.value !== selectedOption(),
              'max-w-28': option.value === selectedOption()
            }"
          >
            <label
              [for]="option.id ?? (id + '-option-' + i)"
              class="w-full p-2 cursor-pointer"
              [ngClass]="{
                'hidden': !showExtendedColumnOptions() && selectedOptionIndex() >= 3
              }"
            >
              <span
                class="overflow-hidden text-sm font-light tracking-wide text-center transition-opacity duration-300 whitespace-nowrap text-nowrap text-ellipsis"
                [ngClass]="{
                  'opacity-0': option.value !== selectedOption(),
                  'opacity-1': option.value === selectedOption()
                }"
              >
                {{ ! option.label || option.label.length === 0 ? 'Unnamed' : option.label }}
              </span>
            </label>
          </div>

          @if (option.value !== selectedOption()) {
            <nrc-tooltip
              class="tooltip tooltip-group"
              [text]="!option.label || option.label.length === 0 ? 'Unknown' : option.label"
            />
          }
        </div>
      }
    </div>

    <!-- Only display the description of the selected option underneath all available options -->
    @if (selectedOption() && selectedOptionDescription()) {
      <span class="flex pt-2 text-xs font-light text-white/50 text-pretty">
        {{ selectedOptionDescription() }}
      </span>
    }
  }
}
